import React, { useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { RotateCw, Paperclip, Upload, Send, Trash } from 'lucide-react';
import styles from './ChatInput.module.scss';
import { logInfo } from "./utils/logger";

const COMPONENT_NAME = 'ChatInput';

const ChatInput = forwardRef(({
  message,
  setMessage,
  onSend,
  onFileClick,
  onDuplicateLastMessage,
  onClearAllMessages,
  isDragging,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  hasMessages,
  attachedFilesCount
}, ref) => {
  const textareaRef = useRef(null);

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }, [onSend]);

  const resetTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, []);

  const handleClearAllMessages = useCallback(() => {
    logInfo('Clear all icon clicked', COMPONENT_NAME);
    onClearAllMessages();
  }, [onClearAllMessages]);

  useImperativeHandle(ref, () => ({
    resetHeight: resetTextareaHeight,
    focus: () => textareaRef.current?.focus()
  }));

  return (
    <div
      className={`${styles.chatInput} ${isDragging ? styles.chatInputDragActive : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {isDragging && (
        <div className={styles.dropOverlay}>
          <Upload size={32} className={styles.dropOverlayIcon} />
          <span className={styles.dropOverlayText}>Drop files to upload</span>
        </div>
      )}

      <div className={styles.textareaWrapper}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Type a message..."
          className={styles.chatTextarea}
          rows={1}
        />

        <div className={styles.buttonContainer}>
          {hasMessages && (
            <button
              onClick={onDuplicateLastMessage}
              className={styles.actionButton}
              title="Duplicate last message"
            >
              <RotateCw size={17} />
            </button>
          )}

          {hasMessages && (
            <button
              onClick={handleClearAllMessages}
              className={styles.actionButton}
              title="Clear all chat history"
            >
              <Trash size={17} />
            </button>
          )}

          <button
            onClick={onFileClick}
            className={styles.fileUploadButton}
            title="Attach files"
          >
            <Paperclip size={17} />
          </button>

          <button
            onClick={onSend}
            disabled={!message.trim() && attachedFilesCount === 0}
            className={`${styles.sendButton} ${(!message.trim() && attachedFilesCount === 0) ? styles.sendButtonDisabled : ''}`}
            title="Send message"
          >
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
});

ChatInput.propTypes = {
  message: PropTypes.string.isRequired,
  setMessage: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  onDuplicateLastMessage: PropTypes.func.isRequired,
  onClearAllMessages: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  hasMessages: PropTypes.bool.isRequired,
  attachedFilesCount: PropTypes.number.isRequired
};

export default ChatInput;
